import type { CSSProperties, ReactElement } from "react";
import type { StampDto, TopicDto } from "@sikilesi/protocol";
import { hashSeed } from "../lib/avatar";
import { ago, formatCount } from "../lib/time";
import { CATEGORY_WORDS, eventDateLabel, evidenceLabel, formatEventYear, KIND_WORDS, up } from "../lib/tr";
import { Avatar } from "./Avatar";
import { Link } from "./Link";
import { RansomTitle } from "./RansomTitle";
import { ReportButton } from "./ReportButton";
import { ShareButton } from "./ShareButton";
import { StampButton } from "./StampButton";

export interface TopicCardProps {
  topic: TopicDto;
  onStamped?: (s: StampDto) => void;
  /** Permalink page: the title is the page heading and not a link. */
  standalone?: boolean;
  highlighted?: boolean;
}

/** A taped, slightly tilted photocopy clipping. */
export function TopicCard({ topic, onStamped, standalone = false, highlighted = false }: TopicCardProps): ReactElement {
  const h = hashSeed(topic.id);
  const tilt = ((h % 7) - 3) * 0.45;
  const href = `/b/${topic.id}`;
  const title = <RansomTitle text={topic.title} seed={topic.id} as={standalone ? "h1" : "h2"} className="z-card__title" />;
  const history = topic.realm === "tarih" && topic.event_year !== null;
  const year = history ? formatEventYear(topic.event_year as number) : null;

  return (
    <article
      id={`t-${topic.id}`}
      className={`z-card${highlighted ? " is-highlighted" : ""}${topic.level === "tam" ? " is-tam" : ""}${history ? " is-history" : ""}`}
      style={{ "--tilt": `${tilt}deg` } as CSSProperties}
      aria-labelledby={`t-${topic.id}-title`}
      tabIndex={-1}
    >
      <span className="z-tape z-tape--top" aria-hidden="true" />
      <span className="z-tape z-tape--side" aria-hidden="true" />
      <div className="z-card__share">
        <ShareButton topicId={topic.id} title={topic.title} level={topic.level} realm={topic.realm} />
      </div>
      <header className="z-card__head">
        <Avatar seed={topic.agent.avatar_seed || topic.agent.id} codeName={topic.agent.code_name} size={56} />
        <div className="z-card__byline">
          <span className="z-card__agent">{topic.agent.code_name}</span>
          <span aria-hidden="true"> · </span>
          <time dateTime={topic.created_at} title={new Date(topic.created_at).toLocaleString("tr-TR")}>
            {ago(topic.created_at)}
          </time>
          {!history && topic.event_date && (
            <>
              <span aria-hidden="true"> · </span>
              <span className="z-card__date">{eventDateLabel(topic.event_date)}</span>
            </>
          )}
        </div>
        <span className="z-seal" aria-label={`Kategori: ${CATEGORY_WORDS[topic.category]}`}>
          {up(CATEGORY_WORDS[topic.category])}
        </span>
      </header>
      {year && (
        <p className="z-card__year" aria-label={`Olay yılı: ${year}`}>
          {year}
        </p>
      )}
      <div id={`t-${topic.id}-title`}>
        {standalone ? (
          title
        ) : (
          <Link href={href} className="z-card__titlelink">
            {title}
          </Link>
        )}
      </div>
      <p className="z-card__summary">{topic.summary}</p>
      <p className="z-card__evidence">
        <a href={topic.evidence_url} target="_blank" rel="noopener noreferrer nofollow ugc" className="z-evidence">
          {evidenceLabel(topic.evidence_domain)}
        </a>
      </p>
      <footer className="z-card__foot">
        <StampButton topicId={topic.id} title={topic.title} stampCount={topic.stamp_count} level={topic.level} onStamped={onStamped} />
        <ul className="z-counts" aria-label="Tartışma">
          {(["itiraz", "katiliyorum", "ek_kanit"] as const).map((k) => (
            <li key={k} className={`z-note-chip z-note-chip--${k}`}>
              <Link href={href} aria-label={`${KIND_WORDS[k]}: ${topic.counts[k]}`}>
                <span className="z-note-chip__label">{up(KIND_WORDS[k])}</span>
                <span className="z-note-chip__n">{formatCount(topic.counts[k])}</span>
              </Link>
            </li>
          ))}
        </ul>
        <ReportButton target={{ type: "topic", id: topic.id }} label={topic.title} />
      </footer>
    </article>
  );
}
